import type { ApolloError } from '@apollo/client';
import { redirect, type ActionFunction } from 'react-router-dom';
import { client } from '~/api/client';
import { mutateData } from '~/api/data/request';
import { submitMutation } from '~/api/data/responses';
import {
  CreateTriggerDefinitionDocument,
  CreateTriggerDefinitionMutation,
  CreateTriggerDefinitionMutationVariables,
  GetTriggerDefinitionsDocument,
  UpdateTriggerDefinitionDocument,
  UpdateTriggerDefinitionMutation,
  UpdateTriggerDefinitionMutationVariables
} from '~/api/graphql';

export const createTriggerDefinition = async (
  input: CreateTriggerDefinitionMutationVariables['input']
) => {
  const result = await mutateData<
    CreateTriggerDefinitionMutation,
    CreateTriggerDefinitionMutationVariables
  >(client, {
    mutation: CreateTriggerDefinitionDocument,
    variables: { input }
  });
  if (result.data?.createTriggerDefinition?.id) {
    // A list refresh cannot invalidate a completed creation. The list route
    // handles its own load errors without making the mutation look replayable.
    await client
      .query({ query: GetTriggerDefinitionsDocument, fetchPolicy: 'network-only' })
      .catch(() => undefined);
  }
  return result;
};

export const updateTriggerDefinition = (input: UpdateTriggerDefinitionMutationVariables['input']) =>
  mutateData<UpdateTriggerDefinitionMutation, UpdateTriggerDefinitionMutationVariables>(client, {
    mutation: UpdateTriggerDefinitionDocument,
    variables: { input },
    refetchQueries: [{ query: GetTriggerDefinitionsDocument }],
    awaitRefetchQueries: true
  });

// A failure the form can explain, rather than one that replaces the page.
const asServerError = (serverError: unknown) => ({ serverError: serverError as ApolloError });

export const submitCreateTriggerDefinition: ActionFunction = async ({ request }) =>
  submitMutation(createTriggerDefinition(await request.json()), () =>
    redirect('/app/workflows/triggers')
  ).catch(asServerError);

export const submitUpdateTriggerDefinition: ActionFunction = async ({ request }) =>
  submitMutation(updateTriggerDefinition(await request.json()), () =>
    redirect('/app/workflows/triggers')
  ).catch(asServerError);
